export type Visibility = 'private' | 'public'
export type AttributionMode = 'named' | 'anonymous'
export const OPENING_LYRIC_MAX_LENGTH = 160

/**
 * `end`, `defaultNext`, and `repeat` remain optional solely so saved v1/v2
 * works can be read and migrated in memory. New saves always use v3 endpoints.
 */
export interface ProjectNode {
  id: string
  label: string
  start: number
  openingLyric?: string
  end?: number
  defaultNext?: string | null
  repeat?: boolean
}

export interface DerivedProjectNode extends Omit<ProjectNode, 'end'> {
  end: number | null
}

export interface ProjectEdge {
  from: string
  to: string
  label: string
  order: number
}

export interface ProjectConfig {
  schemaVersion: 1 | 2 | 3
  video: {
    provider: 'youtube'
    videoId: string
    duration: number | null
    title: string
    channelTitle: string
  }
  nodes: ProjectNode[]
  edges: ProjectEdge[]
  playbackSettings: {
    initialNodeId: string | null
    endToleranceSeconds: number
    seekToleranceSeconds: number
    seekTimeoutMs: number
    autoplay: false
    showYouTubeControls: true
  }
}

export interface ProjectSummary {
  id: string
  title: string
  description: string
  youtubeVideoId: string
  youtubeTitle: string | null
  youtubeChannelTitle: string | null
  visibility: Visibility
  attributionMode: AttributionMode
  moderationStatus: 'active' | 'hidden'
  shareSlug: string | null
  revision: number
  updatedAt: string
  publishedAt: string | null
  creatorName?: string
  sourceTitle?: string | null
  videoSuspended?: boolean
  favoriteCount?: number
  isFavorited?: boolean
  metricValue?: number
  metricType?: 'favorites' | 'views'
  metricPeriod?: 'day' | 'week' | 'month'
  /** Cumulative public share-page entries for this YouTube video. */
  playCount?: number
}

export interface ProjectDetail extends ProjectSummary {
  config: ProjectConfig
  sourceProjectId: string | null
  createdAt: string
}

export function parseYouTubeVideoId(input: string): string | null {
  const value = input.trim()
  if (/^[A-Za-z0-9_-]{11}$/.test(value)) return value
  try {
    const url = new URL(value)
    const host = url.hostname.replace(/^www\./, '')
    let candidate: string | null = null
    if (host === 'youtu.be') candidate = url.pathname.split('/').filter(Boolean)[0] ?? null
    else if (['youtube.com', 'm.youtube.com', 'music.youtube.com', 'youtube-nocookie.com'].includes(host)) {
      if (url.pathname === '/watch') candidate = url.searchParams.get('v')
      else {
        const parts = url.pathname.split('/').filter(Boolean)
        if (['embed', 'shorts', 'live'].includes(parts[0])) candidate = parts[1] ?? null
      }
    }
    return candidate && /^[A-Za-z0-9_-]{11}$/.test(candidate) ? candidate : null
  } catch {
    return null
  }
}

const sortNodes = (nodes: ProjectNode[]) => [...nodes].sort((a, b) => a.start - b.start || a.id.localeCompare(b.id))
const hasExplicitEnd = (node: ProjectNode, duration: number | null) =>
  Number.isFinite(node.end) && Number.isFinite(duration) && node.start < (node.end as number) && (node.end as number) <= (duration as number)

/** Kept for UI ordering; explicit v3 endpoints are never recalculated here. */
export function deriveProjectNodes(nodes: ProjectNode[], duration: number | null): DerivedProjectNode[] {
  void duration
  return sortNodes(nodes).map((node) => ({ ...node, end: Number.isFinite(node.end) ? node.end as number : null }))
}

/**
 * Converts v1/v2 segments to explicit endpoints in memory. It deliberately
 * does not make a request or indicate unsaved changes; the caller decides when
 * the migrated v3 document is persisted.
 */
export function normalizeProjectConfig(config: ProjectConfig): ProjectConfig {
  const legacy = config.schemaVersion < 3
  const ordered = sortNodes(config.nodes)
  return {
    ...config,
    schemaVersion: 3,
    nodes: ordered.map((node, index) => {
      const migrated: ProjectNode = { ...node }
      if (typeof migrated.openingLyric === 'string') {
        const openingLyric = migrated.openingLyric.trim()
        if (openingLyric) migrated.openingLyric = openingLyric
        else delete migrated.openingLyric
      }
      if (legacy) {
        const fallbackEnd = ordered[index + 1]?.start ?? config.video.duration
        if (!hasExplicitEnd(migrated, config.video.duration) && Number.isFinite(fallbackEnd)) migrated.end = fallbackEnd as number
      }
      delete migrated.defaultNext
      delete migrated.repeat
      return migrated
    }),
  }
}

export function validPlaybackNodes(nodes: ProjectNode[], duration: number | null): Array<ProjectNode & { end: number }> {
  if (!Number.isFinite(duration) || (duration ?? 0) <= 0) return []
  const candidates: Array<ProjectNode & { end: number }> = []
  for (const node of sortNodes(nodes)) {
    const end = node.end
    if (Number.isFinite(node.start) && Number.isFinite(end) && node.start >= 0 && node.start < (end as number) && (end as number) <= (duration as number)) {
      candidates.push({ ...node, end: end as number })
    }
  }
  return candidates
}

export type PackedPlaybackNode = ProjectNode & {
  end: number
  logicalLane: number
  visualLane: 0 | 1
  aggregated: boolean
}

export type PlaybackOverlapGroup = {
  start: number
  end: number
  overflowCount: number
}

/**
 * Deterministic half-open interval packing for the compact whole-song rail.
 * Logical lanes preserve the true overlap depth; presentation is capped at
 * two visual lanes and logical lane 3+ is rendered as an aggregate layer.
 */
export function packPlaybackNodeLanes(
  nodes: ProjectNode[],
  duration: number | null,
): { nodes: PackedPlaybackNode[]; laneCount: 1 | 2; maxLogicalLaneCount: number; overlapGroups: PlaybackOverlapGroup[] } {
  const ordered = validPlaybackNodes(nodes, duration)
  const laneEnds: number[] = []
  const packed = ordered.map((node) => {
    let logicalLane = laneEnds.findIndex((end) => end <= node.start)
    if (logicalLane < 0) logicalLane = laneEnds.length
    laneEnds[logicalLane] = node.end
    return {
      ...node,
      logicalLane,
      visualLane: logicalLane === 0 ? 0 as const : 1 as const,
      aggregated: logicalLane >= 2,
    }
  })
  const aggregated = packed.filter((node) => node.aggregated)
  const overlapGroups: PlaybackOverlapGroup[] = []
  for (const node of aggregated) {
    const current = overlapGroups.at(-1)
    if (current && node.start < current.end) {
      current.end = Math.max(current.end, node.end)
      current.overflowCount += 1
    } else {
      overlapGroups.push({ start: node.start, end: node.end, overflowCount: 1 })
    }
  }
  return {
    nodes: packed,
    laneCount: laneEnds.length > 1 ? 2 : 1,
    maxLogicalLaneCount: laneEnds.length,
    overlapGroups,
  }
}

export function findPlaybackNodeAtTime(
  nodes: ProjectNode[],
  duration: number | null,
  time: number,
  preferredNodeId?: string | null,
) {
  const matches = validPlaybackNodes(nodes, duration).filter((node) => time >= node.start && time < node.end)
  if (!matches.length) return null
  if (preferredNodeId) {
    const preferred = matches.find((node) => node.id === preferredNodeId)
    if (preferred) return preferred
  }
  return matches[matches.length - 1] ?? null
}

export function validateProjectConfig(config: ProjectConfig): string[] {
  const errors: string[] = []
  if (![1, 2, 3].includes(config.schemaVersion)) errors.push('作品設定版本不正確')
  if (!parseYouTubeVideoId(config.video.videoId)) errors.push('YouTube Video ID 無效')
  if (!Number.isFinite(config.video.duration) || (config.video.duration ?? 0) <= 0) errors.push('尚未取得影片總長度')
  if (!config.video.title.trim()) errors.push('尚未取得歌曲名稱')
  if (!config.nodes.length) errors.push('請至少新增一個段落')
  if (config.nodes.length > 100) errors.push('節點不可超過 100 個')
  if (config.edges.length > 300) errors.push('連接不可超過 300 條')

  const nodeIds = new Set<string>()
  const duration = config.video.duration ?? 0
  for (const node of config.nodes) {
    if (!node.id || nodeIds.has(node.id)) errors.push('節點 ID 必須存在且不可重複')
    nodeIds.add(node.id)
    if (!node.label.trim() || node.label.length > 60) errors.push('節點名稱需為 1–60 字')
    if (typeof node.openingLyric === 'string' && node.openingLyric.length > OPENING_LYRIC_MAX_LENGTH) errors.push(`開頭歌詞不可超過 ${OPENING_LYRIC_MAX_LENGTH} 字`)
    if (!Number.isFinite(node.start) || node.start < 0) errors.push(`節點「${node.label || node.id}」的開始秒數不可為負值`)
    const end = node.end
    if (!Number.isFinite(end)) errors.push('請設定結束時間。')
    else if (!Number.isFinite(node.start) || node.start >= (end as number)) errors.push('開始時間必須早於結束時間。')
    else if ((end as number) > duration) errors.push('結束時間不得晚於影片結束時間。')
  }
  for (const edge of config.edges) {
    if (!nodeIds.has(edge.from) || !nodeIds.has(edge.to)) errors.push('連接引用了不存在的節點')
  }
  if (config.playbackSettings.initialNodeId && !nodeIds.has(config.playbackSettings.initialNodeId)) errors.push('起始節點不存在')
  if (JSON.stringify(config).length > 256 * 1024) errors.push('作品設定不可超過 256 KB')
  return [...new Set(errors)]
}

/** Informational only. Natural playback must never use this result as a seek target. */
export function findNaturalNextNode(
  nodes: ProjectNode[],
  duration: number | null,
  currentNodeId: string,
) {
  const ordered = validPlaybackNodes(nodes, duration)
  const currentIndex = ordered.findIndex((node) => node.id === currentNodeId)
  return currentIndex >= 0 ? ordered[currentIndex + 1] ?? null : null
}

/** The only helper that may produce a programmatic queued seek target. */
export function resolveQueuedTarget(
  queuedNextId: string | null,
  availableNodeIds: Set<string>,
) {
  return queuedNextId && availableNodeIds.has(queuedNextId) ? queuedNextId : null
}

export function createInitialConfig(videoId: string): ProjectConfig {
  return {
    schemaVersion: 3,
    video: { provider: 'youtube', videoId, duration: null, title: '', channelTitle: '' },
    nodes: [],
    edges: [],
    playbackSettings: {
      initialNodeId: null,
      endToleranceSeconds: 0.12,
      seekToleranceSeconds: 1.5,
      seekTimeoutMs: 2500,
      autoplay: false,
      showYouTubeControls: true,
    },
  }
}
